import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2, MessageCircle, ShieldCheck, CalendarDays, MapPin,
  CreditCard, Banknote, QrCode, AlertTriangle, Package, User, ClipboardCheck, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { fmtBRL, buildWhatsAppUrl } from "@/lib/format";
import { imgVariant } from "@/lib/img";
import { trackInitiateCheckout, trackContact } from "@/lib/tracking";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// ---------- helpers
const onlyDigits = (v) => (v || "").replace(/\D/g, "");

export function validCPF(cpf) {
  const d = onlyDigits(cpf);
  if (d.length !== 11 || new Set(d).size === 1) return false;
  for (const t of [9, 10]) {
    let s = 0;
    for (let i = 0; i < t; i++) s += parseInt(d[i]) * (t + 1 - i);
    if ((s * 10) % 11 % 10 !== parseInt(d[t])) return false;
  }
  return true;
}

const maskCPF = (v) =>
  onlyDigits(v).slice(0, 11).replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const maskPhone = (v) =>
  onlyDigits(v).slice(0, 11).replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");

const maskCEP = (v) => onlyDigits(v).slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];

const CHECKOUT_STEPS = [
  { n: 1, label: "Seus dados", icon: User },
  { n: 2, label: "Entrega", icon: MapPin },
  { n: 3, label: "Pagamento", icon: CreditCard },
  { n: 4, label: "Revisão", icon: ClipboardCheck },
];

const PAYMENT_METHODS = [
  { id: "pix", label: "Pix", desc: "Preço à vista, pago na entrega", icon: QrCode },
  { id: "dinheiro", label: "Dinheiro", desc: "Preço à vista, pago na entrega", icon: Banknote },
  { id: "debito", label: "Cartão de Débito", desc: "Preço à vista, pago na entrega", icon: CreditCard },
  { id: "credito", label: "Cartão de Crédito", desc: "Parcelado, pago na entrega", icon: CreditCard },
  { id: "combinado", label: "Pagamento Combinado", desc: "Duas modalidades — vale o valor do cartão de crédito", icon: CreditCard },
];
const METHOD_SHORT = { pix: "Pix", dinheiro: "Dinheiro", debito: "Débito", credito: "Crédito" };

function CalendarPicker({ availability, selected, onSelect }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const base = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + monthOffset);
    return d;
  }, [monthOffset]);

  const availMap = useMemo(() => {
    const m = {};
    availability.forEach((a) => (m[a.date] = a.available));
    return m;
  }, [availability]);

  const year = base.getFullYear();
  const month = base.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-white" data-testid="delivery-calendar">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => setMonthOffset((m) => Math.max(0, m - 1))}
          className="px-3 py-1 text-sm rounded-full hover:bg-gray-100 disabled:opacity-30" disabled={monthOffset === 0}
          data-testid="calendar-prev">←</button>
        <p className="font-heading font-semibold text-gray-900 capitalize">{MONTHS[month]} de {year}</p>
        <button type="button" onClick={() => setMonthOffset((m) => Math.min(1, m + 1))}
          className="px-3 py-1 text-sm rounded-full hover:bg-gray-100"
          data-testid="calendar-next">→</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-400 mb-1">
        {WEEKDAYS.map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <span key={`e${i}`} />;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const available = availMap[iso] === true;
          const isSelected = selected === iso;
          return (
            <button
              key={iso}
              type="button"
              disabled={!available}
              onClick={() => onSelect(iso)}
              className={`h-9 rounded-lg text-sm font-medium transition-colors ${
                isSelected
                  ? "bg-brand text-white"
                  : available
                    ? "bg-green-50 text-green-800 hover:bg-green-100"
                    : "text-gray-300 cursor-not-allowed"
              }`}
              data-testid={`calendar-day-${iso}`}
            >
              {day}
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-gray-500 flex items-center gap-1.5">
        <CalendarDays className="w-3.5 h-3.5 text-brand" />
        Entregas de segunda a sábado, em horário comercial. A data é confirmada pela nossa equipe.
      </p>
    </div>
  );
}

export default function CheckoutPage() {
  const { items, clearCart } = useCart();
  const settings = useSettings();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", cpf: "", phone: "", email: "",
    cep: "", street: "", number: "", complement: "",
    neighborhood: "", city: "", state: "", reference: "", note: "",
  });
  const [cepLoading, setCepLoading] = useState(false);
  const [coverage, setCoverage] = useState(null); // {covered, fee, region}
  const [availability, setAvailability] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [step, setStep] = useState(1); // 1 dados | 2 endereço+entrega | 3 pagamento | 4 revisão
  const [paymentMethod, setPaymentMethod] = useState(""); // nenhuma forma pré-selecionada
  const [installments, setInstallments] = useState(null); // crédito exige escolha consciente
  const [cashMethod, setCashMethod] = useState("pix");
  const [secondMethod, setSecondMethod] = useState("credito");
  const [cashAmount, setCashAmount] = useState("");
  const [combinedConfirmed, setCombinedConfirmed] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(null);
  const quoteTimer = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/delivery/availability").then((r) => setAvailability(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length > 0 && !done) trackInitiateCheckout(items.reduce((s, i) => s + i.price * i.qty, 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (items.length === 0 && !done) navigate("/carrinho");
  }, [items.length, done, navigate]);

  // ViaCEP — preenche o endereço automaticamente
  const lookupCep = async (cepValue) => {
    const cep = onlyDigits(cepValue);
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm((f) => ({
          ...f,
          street: data.logradouro || f.street,
          neighborhood: data.bairro || f.neighborhood,
          city: data.localidade || f.city,
          state: data.uf || f.state,
        }));
      } else {
        toast.error("CEP não encontrado. Preencha o endereço manualmente.");
      }
    } catch {
      toast.error("Não foi possível consultar o CEP. Preencha manualmente.");
    } finally {
      setCepLoading(false);
    }
  };

  // aproveita o CEP informado na página do produto ou no carrinho
  useEffect(() => {
    const saved = localStorage.getItem("hg_delivery_cep");
    if (saved && onlyDigits(saved).length === 8) {
      setForm((f) => (f.cep ? f : { ...f, cep: maskCEP(saved) }));
      lookupCep(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // cliente logado: preenche dados e endereço padrão (opcional — pode editar tudo)
  useEffect(() => {
    api.get("/account/me").then(({ data: c }) => {
      const addr = (c.addresses || []).find((a) => a.is_default) || c.addresses?.[0];
      setForm((f) => ({
        ...f,
        name: f.name || c.name || "",
        cpf: f.cpf || (c.cpf ? maskCPF(c.cpf) : ""),
        phone: f.phone || (c.phone ? maskPhone(c.phone) : ""),
        email: f.email || c.email || "",
        ...(addr
          ? {
              cep: f.cep || maskCEP(addr.cep), street: f.street || addr.street || "",
              number: f.number || addr.number || "", complement: f.complement || addr.complement || "",
              neighborhood: f.neighborhood || addr.neighborhood || "", city: f.city || addr.city || "",
              state: f.state || addr.state || "", reference: f.reference || addr.reference || "",
            }
          : {}),
      }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // verificação de cobertura
  useEffect(() => {
    const cep = onlyDigits(form.cep);
    if (!form.city.trim() && cep.length < 8) {
      setCoverage(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .get("/delivery/check", {
          params: { cep, city: form.city, neighborhood: form.neighborhood },
        })
        .then((r) => setCoverage(r.data))
        .catch(() => setCoverage(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.cep, form.city, form.neighborhood]);

  const cartItems = useMemo(
    () => items.map((i) => ({ product_id: i.id, color: i.color, qty: i.qty })),
    [items]
  );

  // cotação no servidor (preços, frete e parcelas calculados no backend)
  useEffect(() => {
    if (!paymentMethod || !coverage?.covered || !deliveryDate || cartItems.length === 0) {
      setQuote(null);
      return;
    }
    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(() => {
      // no combinado, a cotação base vem do Pix para obter as duas tabelas
      const method = paymentMethod === "combinado" ? "pix" : paymentMethod;
      api
        .post("/orders/quote", {
          payment_method: method,
          installments: installments || 1,
          cep: onlyDigits(form.cep),
          city: form.city,
          neighborhood: form.neighborhood,
          delivery_date: deliveryDate,
          items: cartItems,
        })
        .then((r) => {
          setQuote(r.data);
          setQuoteError("");
        })
        .catch((err) => {
          setQuote(null);
          setQuoteError(formatApiError(err, "Não foi possível calcular o pedido."));
        });
    }, 350);
    return () => clearTimeout(quoteTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverage, deliveryDate, paymentMethod, installments, cartItems]);

  // cálculo do combinado (exibição; validação final no servidor)
  // regra: em qualquer pagamento combinado prevalece o valor total do cartão de crédito
  const combined = useMemo(() => {
    if (paymentMethod !== "combinado" || !quote) return null;
    const a = parseFloat(cashAmount) || 0;
    const cardTotal = quote.card_total;
    const b = Math.round((cardTotal - a) * 100) / 100;
    if (a <= 0 || b <= 0) return { valid: false, a, b, total: 0 };
    const total = Math.round((cardTotal + quote.delivery_fee) * 100) / 100;
    return { valid: true, a, b, total };
  }, [paymentMethod, quote, cashAmount]);

  const combinedHasCredit = paymentMethod === "combinado" && (cashMethod === "credito" || secondMethod === "credito");

  const displayTotal =
    paymentMethod === "combinado" && combined?.valid ? combined.total : quote?.total;

  const validateStep = (n) => {
    if (n === 1) {
      if (!form.name.trim()) return "Informe seu nome completo.";
      if (!validCPF(form.cpf)) return "CPF inválido. Confira os números digitados.";
      if (onlyDigits(form.phone).length < 10) return "Informe um telefone válido com DDD.";
    }
    if (n === 2) {
      if (onlyDigits(form.cep).length !== 8) return "Informe um CEP válido.";
      if (!form.street.trim() || !form.number.trim() || !form.neighborhood.trim() || !form.city.trim() || !form.state.trim())
        return "Complete o endereço de entrega.";
      if (!coverage?.covered)
        return "Ainda não entregamos nessa região. Fale com a equipe pelo WhatsApp.";
      if (!deliveryDate) return "Escolha a data desejada para a entrega.";
    }
    if (n === 3) {
      if (!paymentMethod) return "Escolha a forma de pagamento para continuar.";
      if (paymentMethod === "credito" && !installments) return "Selecione o número de parcelas.";
      if (paymentMethod === "combinado") {
        if (!combined?.valid) return "Informe o valor da primeira parte para dividir o pagamento.";
        if (combinedHasCredit && !installments) return "Selecione o número de parcelas da parte no crédito.";
        if (!combinedConfirmed) return "Confirme os valores do pagamento combinado.";
      }
      if (!quote) return quoteError || "Aguarde o cálculo do pedido.";
    }
    return null;
  };

  const goNext = () => {
    const error = validateStep(step);
    if (error) {
      toast.error(error);
      return;
    }
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo(0, 0);
  };
  const goBack = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo(0, 0);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (step !== 4) return; // só confirma na etapa de revisão
    const error = [1, 2, 3].map(validateStep).find(Boolean);
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer: {
          name: form.name.trim(),
          cpf: onlyDigits(form.cpf),
          phone: onlyDigits(form.phone),
          email: form.email.trim() || null,
          cep: onlyDigits(form.cep),
          street: form.street.trim(),
          number: form.number.trim(),
          complement: form.complement.trim() || null,
          neighborhood: form.neighborhood.trim(),
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          reference: form.reference.trim() || null,
          note: form.note.trim() || null,
        },
        payment_method: paymentMethod,
        installments: paymentMethod === "credito" || combinedHasCredit ? (installments || 1) : 1,
        combined:
          paymentMethod === "combinado"
            ? [
                { method: cashMethod, amount: combined.a },
                { method: secondMethod, amount: combined.b },
              ]
            : [],
        combined_confirmed: paymentMethod === "combinado" ? combinedConfirmed : false,
        delivery_date: deliveryDate,
        items: cartItems,
      };
      const { data: order } = await api.post("/orders", payload);
      // sucesso somente após o backend confirmar a gravação e devolver o pedido salvo
      if (!order?.order_number) throw new Error("Resposta inválida do servidor.");
      trackContact(order.total);
      setDone(order);
      clearCart();
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(formatApiError(err, "Não foi possível registrar o pedido. Tente novamente — nenhum valor foi cobrado."));
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- tela de sucesso
  if (done) {
    const waMessage = [
      `*PEDIDO ${done.order_number} — HG MÓVEIS*`,
      "",
      `Cliente: ${done.customer.name}`,
      `Endereço: ${done.customer.street}, ${done.customer.number} — ${done.customer.neighborhood}, ${done.customer.city}/${done.customer.state}`,
      "",
      "*Itens:*",
      ...done.items.map(
        (i, idx) => `${idx + 1}. ${i.name}${i.color ? ` (${i.color})` : ""} — ${i.qty}x`
      ),
      "",
      `Pagamento: ${done.payment_label}${done.installments > 1 ? ` em ${done.installments}x` : ""}`,
      `Entrega (${done.region_name}): ${fmtBRL(done.delivery_fee)}`,
      `Data desejada: ${done.delivery_date.split("-").reverse().join("/")}`,
      `*TOTAL: ${fmtBRL(done.total)}*`,
    ].join("\n");

    return (
      <div className="container-store py-16 max-w-xl mx-auto text-center" data-testid="checkout-success">
        <Seo title="Pedido Recebido | HG Móveis" noindex />
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
        <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-bold text-gray-900">
          PEDIDO RECEBIDO COM SUCESSO!
        </h1>
        <p className="mt-3 text-gray-600">
          Obrigado por comprar na HG Móveis! Recebemos seu pedido e nossa equipe entrará em contato para confirmar a entrega.
        </p>
        <div className="mt-6 bg-white border border-gray-100 rounded-2xl p-6 text-left">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Número do pedido</span>
            <span className="font-heading font-bold text-xl text-brand" data-testid="order-number">
              {done.order_number}
            </span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">Status</span>
            <span className="text-xs font-bold uppercase tracking-wide bg-amber-50 text-amber-700 rounded-full px-3 py-1" data-testid="order-status">
              Aguardando confirmação
            </span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">Total ({done.payment_label})</span>
            <span className="font-heading font-bold text-gray-900">{fmtBRL(done.total)}</span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">Entrega desejada</span>
            <span className="text-sm font-medium text-gray-800">
              {done.delivery_date.split("-").reverse().join("/")}
            </span>
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={`/acompanhar?numero=${done.order_number}`}>
            <Button variant="outline" className="rounded-full px-6 border-brand text-brand" data-testid="success-track">
              <Package className="w-4 h-4" /> Acompanhar pedido
            </Button>
          </Link>
          <a href={buildWhatsAppUrl(waMessage, settings.whatsapp_number)} target="_blank" rel="noreferrer">
            <Button className="rounded-full bg-green-600 hover:bg-green-700 px-6" data-testid="success-whatsapp">
              <MessageCircle className="w-4 h-4" /> Enviar cópia pelo WhatsApp
            </Button>
          </a>
          <Link to="/produtos">
            <Button variant="outline" className="rounded-full px-6" data-testid="success-continue">
              Continuar comprando
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const paymentCfg = settings.payment || {};
  const enabledMethods = PAYMENT_METHODS.filter((m) => {
    if (m.id === "pix") return paymentCfg.pix;
    if (m.id === "dinheiro") return paymentCfg.cash;
    if (m.id === "debito") return paymentCfg.debit;
    if (m.id === "credito") return paymentCfg.credit;
    if (m.id === "combinado") return paymentCfg.combined_enabled;
    return false;
  });

  return (
    <div className="container-store py-8 sm:py-12" data-testid="checkout-page">
      <Seo title="Finalizar Pedido | HG Móveis" noindex />
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
        Finalizar Pedido
      </h1>
      <p className="text-sm text-gray-500 mt-1">
        Sem pagamento online — você paga somente na entrega, após a confirmação da nossa equipe.
      </p>

      {/* indicador de etapas */}
      <ol className="mt-6 flex items-center gap-1 sm:gap-2" data-testid="checkout-stepper">
        {CHECKOUT_STEPS.map((s, idx) => {
          const Icon = s.icon;
          const active = step === s.n;
          const doneStep = step > s.n;
          return (
            <li key={s.n} className="flex items-center gap-1 sm:gap-2 flex-1">
              <button
                type="button"
                onClick={() => s.n < step && setStep(s.n)}
                className={`flex items-center gap-2 rounded-full px-2.5 sm:px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold transition-colors ${
                  active
                    ? "bg-brand text-white"
                    : doneStep
                      ? "bg-green-100 text-green-800 hover:bg-green-200"
                      : "bg-gray-100 text-gray-400"
                }`}
                data-testid={`checkout-step-${s.n}`}
              >
                {doneStep ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                <span className={active || doneStep ? "" : "hidden sm:inline"}>{s.label}</span>
              </button>
              {idx < CHECKOUT_STEPS.length - 1 && <span className="flex-1 h-px bg-gray-200" />}
            </li>
          );
        })}
      </ol>

      <form onSubmit={submit} className="mt-8 grid lg:grid-cols-[1fr_400px] gap-8 items-start">
        <div className="space-y-6">
          {/* ETAPA 1 — dados pessoais */}
          {step === 1 && (
          <section className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="checkout-personal">
            <h2 className="font-heading font-semibold text-lg text-gray-900">Seus dados</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="name">Nome completo *</Label>
                <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="checkout-name" />
              </div>
              <div>
                <Label htmlFor="cpf">CPF *</Label>
                <Input id="cpf" inputMode="numeric" value={form.cpf}
                  onChange={(e) => set("cpf", maskCPF(e.target.value))}
                  placeholder="000.000.000-00" className="mt-1.5" data-testid="checkout-cpf" />
                {form.cpf && !validCPF(form.cpf) && (
                  <p className="text-xs text-red-600 mt-1">CPF inválido</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone">Telefone com DDD *</Label>
                <Input id="phone" inputMode="tel" value={form.phone}
                  onChange={(e) => set("phone", maskPhone(e.target.value))}
                  placeholder="(21) 99999-9999" className="mt-1.5" data-testid="checkout-phone" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email">E-mail (opcional)</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="checkout-email" />
              </div>
            </div>
          </section>
          )}

          {/* ETAPA 2 — endereço e data de entrega */}
          {step === 2 && (
          <>
          <section className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="checkout-address">
            <h2 className="font-heading font-semibold text-lg text-gray-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-brand" /> Endereço de entrega
            </h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cep">CEP *</Label>
                <Input id="cep" inputMode="numeric" value={form.cep}
                  onChange={(e) => set("cep", maskCEP(e.target.value))}
                  onBlur={(e) => lookupCep(e.target.value)}
                  placeholder="00000-000" className="mt-1.5" data-testid="checkout-cep" />
                {cepLoading && <p className="text-xs text-brand mt-1">Consultando CEP...</p>}
              </div>
              <div>
                <Label htmlFor="street">Rua *</Label>
                <Input id="street" value={form.street} onChange={(e) => set("street", e.target.value)} className="mt-1.5" data-testid="checkout-street" />
              </div>
              <div>
                <Label htmlFor="number">Número *</Label>
                <Input id="number" value={form.number} onChange={(e) => set("number", e.target.value)} className="mt-1.5" data-testid="checkout-number" />
              </div>
              <div>
                <Label htmlFor="complement">Complemento (opcional)</Label>
                <Input id="complement" value={form.complement} onChange={(e) => set("complement", e.target.value)} className="mt-1.5" data-testid="checkout-complement" />
              </div>
              <div>
                <Label htmlFor="neighborhood">Bairro *</Label>
                <Input id="neighborhood" value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className="mt-1.5" data-testid="checkout-neighborhood" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label htmlFor="city">Cidade *</Label>
                  <Input id="city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5" data-testid="checkout-city" />
                </div>
                <div>
                  <Label htmlFor="state">UF *</Label>
                  <Input id="state" value={form.state} maxLength={2} onChange={(e) => set("state", e.target.value.toUpperCase())} className="mt-1.5" data-testid="checkout-state" />
                </div>
              </div>
              <div>
                <Label htmlFor="reference">Ponto de referência</Label>
                <Input id="reference" value={form.reference} onChange={(e) => set("reference", e.target.value)} className="mt-1.5" data-testid="checkout-reference" />
              </div>
              <div>
                <Label htmlFor="note">Observações</Label>
                <Input id="note" value={form.note} onChange={(e) => set("note", e.target.value)} className="mt-1.5" data-testid="checkout-note" />
              </div>
            </div>

            {/* cobertura */}
            {coverage && (
              <div
                className={`mt-4 rounded-xl p-3.5 text-sm font-medium flex items-start gap-2 ${
                  coverage.covered ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"
                }`}
                data-testid="coverage-result"
              >
                {coverage.covered ? (
                  <>
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Entregamos na sua região ({coverage.region})! Taxa de entrega:{" "}
                      <strong>{coverage.fee > 0 ? fmtBRL(coverage.fee) : "Grátis"}</strong>
                    </span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      Ainda não entregamos nessa região.{" "}
                      <a
                        href={buildWhatsAppUrl(`Olá! Gostaria de saber se a HG Móveis realiza entregas no meu endereço. Meu CEP é: ${form.cep}.`, settings.whatsapp_number)}
                        target="_blank" rel="noreferrer" className="underline font-semibold"
                        data-testid="coverage-whatsapp"
                      >
                        Consulte a equipe pelo WhatsApp
                      </a>
                    </span>
                  </>
                )}
              </div>
            )}
          </section>

          {/* data de entrega */}
          <section className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="checkout-schedule">
            <h2 className="font-heading font-semibold text-lg text-gray-900">Data desejada para a entrega</h2>
            <div className="mt-4">
              <CalendarPicker availability={availability} selected={deliveryDate} onSelect={setDeliveryDate} />
            </div>
          </section>
          </>
          )}

          {/* ETAPA 3 — pagamento (sem pré-seleção: o cliente escolhe conscientemente) */}
          {step === 3 && (
          <section className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="checkout-payment">
            <h2 className="font-heading font-semibold text-lg text-gray-900">Forma de pagamento na entrega</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {enabledMethods.map((m) => {
                const Icon = m.icon;
                const selected = paymentMethod === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setPaymentMethod(m.id); setInstallments(null); }}
                    aria-pressed={selected}
                    className={`flex items-center gap-3 rounded-xl border-2 p-3.5 text-left transition-all ${
                      selected ? "border-brand bg-brand-light/60" : "border-gray-200 hover:border-brand/40"
                    }`}
                    data-testid={`payment-${m.id}`}
                  >
                    <Icon className={`w-5 h-5 shrink-0 ${selected ? "text-brand" : "text-gray-400"}`} />
                    <span>
                      <span className="block text-sm font-semibold text-gray-900">{m.label}</span>
                      <span className="block text-xs text-gray-500">{m.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* parcelamento (crédito puro ou combinado que inclua crédito) */}
            {(paymentMethod === "credito" || combinedHasCredit) && quote && (
              <div className="mt-4">
                <Label>Parcelas da parte no crédito (soma exata)</Label>
                <Select value={installments ? String(installments) : ""} onValueChange={(v) => setInstallments(Number(v))}>
                  <SelectTrigger className="mt-1.5 sm:w-72" data-testid="installments-select">
                    <SelectValue placeholder="Selecione as parcelas" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: quote.max_installments }, (_, i) => i + 1).map((n) => {
                      const base = paymentMethod === "credito"
                        ? quote.total
                        : (combined?.valid
                            ? (cashMethod === "credito" ? combined.a : combined.b) + quote.delivery_fee
                            : quote.card_total + quote.delivery_fee);
                      const per = Math.floor(Math.round(base * 100) / n) / 100;
                      return (
                        <SelectItem key={n} value={String(n)} data-testid={`installment-${n}`}>
                          {n}x de {fmtBRL(per)}{n === 1 ? " (à vista no cartão)" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* combinado: qualquer par de modalidades — prevalece o valor do crédito */}
            {paymentMethod === "combinado" && quote && (
              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3" data-testid="combined-box">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs sm:text-sm text-amber-900 flex items-start gap-2" data-testid="combined-rule-notice">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <strong>Regra do pagamento combinado:</strong> ao dividir o pagamento em duas modalidades,
                    o valor considerado é o <strong>total no cartão de crédito ({fmtBRL(quote.card_total)})</strong>,
                    mesmo que nenhuma das partes seja no crédito.
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-900">Divida o pagamento em duas modalidades</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Primeira modalidade</Label>
                    <Select
                      value={cashMethod}
                      onValueChange={(v) => {
                        setCashMethod(v);
                        if (secondMethod === v) setSecondMethod(v === "credito" ? "pix" : "credito");
                        setCombinedConfirmed(false);
                        setInstallments(null);
                      }}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="combined-cash-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["pix", "dinheiro", "debito", "credito"].map((m) => (
                          <SelectItem key={m} value={m} data-testid={`combined-method-a-${m}`}>{METHOD_SHORT[m]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Valor da primeira parte (R$)</Label>
                    <Input type="number" min="1" step="0.01" value={cashAmount}
                      onChange={(e) => { setCashAmount(e.target.value); setCombinedConfirmed(false); }}
                      placeholder={`Até ${fmtBRL(quote.card_total)}`} className="mt-1.5" data-testid="combined-cash-amount" />
                  </div>
                  <div>
                    <Label>Segunda modalidade</Label>
                    <Select value={secondMethod} onValueChange={(v) => { setSecondMethod(v); setCombinedConfirmed(false); setInstallments(null); }}>
                      <SelectTrigger className="mt-1.5" data-testid="combined-second-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["pix", "dinheiro", "debito", "credito"].filter((m) => m !== cashMethod).map((m) => (
                          <SelectItem key={m} value={m} data-testid={`combined-method-b-${m}`}>{METHOD_SHORT[m]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Valor da segunda parte</Label>
                    <Input value={combined?.valid ? fmtBRL(combined.b) : "—"} disabled className="mt-1.5 bg-gray-50" data-testid="combined-second-amount" />
                  </div>
                </div>
                {combined?.valid ? (
                  <div className="text-sm space-y-1">
                    <p className="text-gray-700">
                      {METHOD_SHORT[cashMethod]}: <strong>{fmtBRL(combined.a)}</strong>
                    </p>
                    <p className="text-gray-700">
                      {METHOD_SHORT[secondMethod]}: <strong>{fmtBRL(combined.b)}</strong>
                      {secondMethod === "credito" && installments ? ` em ${installments}x` : ""}
                    </p>
                    <p className="text-gray-700">Frete: <strong>{fmtBRL(quote.delivery_fee)}</strong></p>
                    <p className="font-heading font-bold text-gray-900">Total: {fmtBRL(combined.total)}</p>
                    <label className="flex items-start gap-2 pt-2 text-sm text-gray-700 cursor-pointer">
                      <Checkbox checked={combinedConfirmed} onCheckedChange={(v) => setCombinedConfirmed(!!v)} data-testid="combined-confirm" />
                      Confirmo os valores acima e estou ciente de que no pagamento combinado prevalece o valor no cartão de crédito.
                    </label>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    Informe o valor da primeira parte (menor que {fmtBRL(quote.card_total)}) para ver o cálculo completo.
                  </p>
                )}
              </div>
            )}
          </section>
          )}

          {/* ETAPA 4 — revisão e confirmação */}
          {step === 4 && (
          <section className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="checkout-review">
            <h2 className="font-heading font-semibold text-lg text-gray-900">Revise e confirme seu pedido</h2>

            {/* produtos */}
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm font-bold text-gray-800 uppercase tracking-wide">Produtos</p>
              <Link to="/carrinho" className="text-xs text-brand font-semibold inline-flex items-center gap-1" data-testid="review-edit-items">
                <Pencil className="w-3 h-3" /> Editar
              </Link>
            </div>
            <ul className="mt-2 space-y-3">
              {items.map((i) => (
                <li key={`${i.id}-${i.color}`} className="flex items-center gap-3 text-sm">
                  <img src={imgVariant(i.image, "thumb") || "/assets/logo.png"} alt="" className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-100 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-800 font-medium line-clamp-1">{i.name}</p>
                    <p className="text-xs text-gray-500">{i.qty}x{i.color ? ` • Cor: ${i.color}` : ""}</p>
                  </div>
                  <span className="font-semibold text-gray-900 shrink-0">
                    {fmtBRL(((paymentMethod === "credito" || paymentMethod === "combinado") ? i.price_card : i.price) * i.qty)}
                  </span>
                </li>
              ))}
            </ul>

            {/* dados do cliente */}
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-start justify-between gap-3">
              <div className="text-sm">
                <p className="font-bold text-gray-800 uppercase tracking-wide text-xs">Dados do cliente</p>
                <p className="mt-1.5 text-gray-700">{form.name}</p>
                <p className="text-xs text-gray-500">CPF {form.cpf} • {form.phone}{form.email ? ` • ${form.email}` : ""}</p>
              </div>
              <button type="button" onClick={() => setStep(1)} className="text-xs text-brand font-semibold inline-flex items-center gap-1 shrink-0" data-testid="review-edit-personal">
                <Pencil className="w-3 h-3" /> Editar
              </button>
            </div>

            {/* endereço e entrega */}
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-start justify-between gap-3">
              <div className="text-sm">
                <p className="font-bold text-gray-800 uppercase tracking-wide text-xs">Endereço e entrega</p>
                <p className="mt-1.5 text-gray-700">
                  {form.street}, {form.number}{form.complement ? ` — ${form.complement}` : ""}
                </p>
                <p className="text-xs text-gray-500">
                  {form.neighborhood} — {form.city}/{form.state} • CEP {form.cep}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Entrega desejada: <strong className="text-gray-700">{deliveryDate.split("-").reverse().join("/")}</strong>
                  {coverage?.region ? ` • ${coverage.region}` : ""} • Frete {fmtBRL(quote?.delivery_fee ?? 0)}
                </p>
              </div>
              <button type="button" onClick={() => setStep(2)} className="text-xs text-brand font-semibold inline-flex items-center gap-1 shrink-0" data-testid="review-edit-address">
                <Pencil className="w-3 h-3" /> Editar
              </button>
            </div>

            {/* pagamento */}
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-start justify-between gap-3">
              <div className="text-sm">
                <p className="font-bold text-gray-800 uppercase tracking-wide text-xs">Forma de pagamento</p>
                <p className="mt-1.5 text-gray-700" data-testid="review-payment">
                  {PAYMENT_METHODS.find((m) => m.id === paymentMethod)?.label} na entrega
                </p>
                {paymentMethod === "credito" && installments && quote && (
                  <p className="text-xs text-gray-500">
                    {installments}x de {fmtBRL(quote.installment_values?.[0] ?? quote.total / installments)}
                  </p>
                )}
                {paymentMethod === "combinado" && combined?.valid && (
                  <p className="text-xs text-gray-500">
                    {METHOD_SHORT[cashMethod]}: {fmtBRL(combined.a)} • {METHOD_SHORT[secondMethod]}{combinedHasCredit && installments ? ` em ${installments}x` : ""}: {fmtBRL(combined.b)}
                  </p>
                )}
              </div>
              <button type="button" onClick={() => setStep(3)} className="text-xs text-brand font-semibold inline-flex items-center gap-1 shrink-0" data-testid="review-edit-payment">
                <Pencil className="w-3 h-3" /> Editar
              </button>
            </div>

            {/* total final */}
            <div className="mt-5 pt-4 border-t border-gray-100 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal dos produtos</span>
                <span>{quote ? fmtBRL(paymentMethod === "combinado" && combined?.valid ? quote.card_total : quote.subtotal) : "—"}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Frete{coverage?.region ? ` (${coverage.region})` : ""}</span>
                <span>{quote ? fmtBRL(quote.delivery_fee) : "—"}</span>
              </div>
              <div className="flex justify-between font-heading font-bold text-xl text-gray-900 pt-2">
                <span>Total final</span>
                <span className="text-brand" data-testid="review-total">{displayTotal != null ? fmtBRL(displayTotal) : "—"}</span>
              </div>
              {paymentMethod === "credito" && installments > 1 && quote && (
                <p className="text-xs text-gray-500">{installments}x de {fmtBRL(quote.installment_values?.[0] ?? quote.total / installments)} no cartão na entrega</p>
              )}
            </div>

            {submitError && (
              <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3.5" data-testid="checkout-submit-error">
                {submitError}
              </p>
            )}

            <label className="mt-4 flex items-start gap-2.5 text-sm text-gray-700 bg-slate-50 border border-slate-200 rounded-xl p-3.5 cursor-pointer" data-testid="checkout-terms-box">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#07335C]"
                data-testid="checkout-terms-checkbox"
              />
              <span>
                Li e concordo com os{" "}
                <Link to="/pagina/termos-e-condicoes" target="_blank" className="text-brand font-medium hover:underline" data-testid="checkout-terms-link">
                  Termos e Condições
                </Link>{" "}
                da HG Móveis.
              </span>
            </label>

            <Button
              type="submit"
              size="lg"
              className="mt-5 w-full rounded-full h-13 font-heading font-bold uppercase tracking-wide active:scale-95 transition-transform"
              disabled={submitting || !termsAccepted}
              data-testid="checkout-submit"
            >
              {submitting ? "Registrando pedido..." : "Confirmar Pedido"}
            </Button>
            {!termsAccepted && (
              <p className="mt-2 text-xs text-amber-700 text-center" data-testid="checkout-terms-hint">
                Marque a caixa acima para confirmar que leu os Termos e Condições.
              </p>
            )}
            <p className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" />
              Ao confirmar, o pedido é registrado com o número gerado pelo servidor e fica "aguardando confirmação"
              até a equipe validar os detalhes. Nenhum dado de cartão é solicitado pelo site.
            </p>
          </section>
          )}

          {/* navegação entre etapas */}
          <div className="flex items-center justify-between gap-3 pt-1">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={goBack} className="rounded-full" data-testid="checkout-back">
                Voltar
              </Button>
            ) : (
              <Link to="/carrinho">
                <Button type="button" variant="outline" className="rounded-full" data-testid="checkout-back-cart">
                  Voltar ao carrinho
                </Button>
              </Link>
            )}
            {step < 4 && (
              <Button type="button" onClick={goNext} className="rounded-full px-8 font-semibold" data-testid="checkout-next">
                Continuar
              </Button>
            )}
          </div>
        </div>

        {/* resumo */}
        <aside className="bg-white border border-gray-100 rounded-2xl p-6 lg:sticky lg:top-40" data-testid="checkout-summary">
          <h2 className="font-heading font-semibold text-lg text-gray-900">Resumo do pedido</h2>
          <ul className="mt-4 space-y-3 text-sm max-h-64 overflow-y-auto">
            {items.map((i) => (
              <li key={`${i.id}-${i.color}`} className="flex gap-3">
                <img src={imgVariant(i.image, "thumb") || "/assets/logo.png"} alt="" loading="lazy" decoding="async" className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-100 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 line-clamp-1">{i.qty}x {i.name}</p>
                  {i.color && <p className="text-xs text-gray-400">{i.color}</p>}
                </div>
                <span className="font-medium text-gray-800 shrink-0">
                  {fmtBRL(((paymentMethod === "credito" || paymentMethod === "combinado") ? i.price_card : i.price) * i.qty)}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span data-testid="checkout-subtotal">
                {paymentMethod === "combinado" && combined?.valid
                  ? fmtBRL(quote.card_total)
                  : quote ? fmtBRL(quote.subtotal) : "—"}
              </span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Entrega{coverage?.region ? ` (${coverage.region})` : ""}</span>
              <span data-testid="checkout-delivery-fee">
                {coverage?.covered ? (quote ? fmtBRL(quote.delivery_fee) : "…") : "A verificar"}
              </span>
            </div>
            <div className="flex justify-between font-heading font-bold text-lg text-gray-900 pt-2">
              <span>Total</span>
              <span className="text-brand" data-testid="checkout-total">
                {displayTotal != null ? fmtBRL(displayTotal) : "—"}
              </span>
            </div>
            {paymentMethod === "credito" && quote && installments > 1 && (
              <p className="text-xs text-gray-500">
                {installments}x de {fmtBRL(quote.total / installments)} no cartão (soma exata do total)
              </p>
            )}
          </div>

          {quoteError && (
            <p className="mt-3 text-xs text-red-600 bg-red-50 rounded-lg p-2.5" data-testid="checkout-quote-error">
              {quoteError}
            </p>
          )}

          <p className="mt-4 text-xs text-gray-500 flex items-start gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" />
            Seu pedido fica como "aguardando confirmação" até a equipe validar os detalhes e a entrega.
            Nenhum dado de cartão é solicitado pelo site.
          </p>
        </aside>
      </form>
    </div>
  );
}
